import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import type { LogEntry } from '../types';
import { logAPI } from '../utils/apiClient';
import logger from '../utils/logger';

import { useAuth } from './AuthContext';

/**
 * LogContext value type
 */
interface LogContextValue {
  log: LogEntry[];
  loading: boolean;
  error: string | null;
  fetchLog: () => Promise<void>;
  logCount: number;
}

/**
 * LogContext - Manages application log state
 *
 * Provides log data and related functionality throughout the application.
 * Handles fetching log entries from the API and maintains state.
 */
const LogContext = createContext<LogContextValue | null>(null);

/**
 * Props for LogProvider component
 */
interface LogProviderProps {
  children: ReactNode;
}

/**
 * Hook to access log context
 * @returns Log context value
 * @throws Error if used outside LogProvider
 */
export const useLog = (): LogContextValue => {
  const context = useContext(LogContext);
  if (!context) {
    throw new Error('useLog must be used within a LogProvider');
  }
  return context;
};

/**
 * LogProvider Component - Provides log state to children
 *
 * Manages log data fetching and state. Automatically fetches log data
 * when user is authenticated and has admin rights.
 */
export const LogProvider: React.FC<LogProviderProps> = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [log, setLog] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch log data from API
   */
  const fetchLog = async (): Promise<void> => {
    if (!user || !isAdmin) {
      setLog([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await logAPI.getLog();

      if (data.success && data.log) {
        setLog(data.log);
      } else {
        throw new Error(data.error || 'Failed to fetch log data');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Error fetching log:', errorMessage);
      setError(errorMessage);
      setLog([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch log data when user changes or becomes admin
   */
  useEffect(() => {
    fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  /**
   * Periodically refresh log data (every 30 seconds)
   */
  useEffect(() => {
    if (!user || !isAdmin) {
      return;
    }
    const interval = setInterval(fetchLog, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const value: LogContextValue = {
    log,
    loading,
    error,
    fetchLog,
    logCount: log.length,
  };

  return <LogContext.Provider value={value}>{children}</LogContext.Provider>;
};

export default LogContext;
